"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";

export type LightboxImage = {
  src: string;
  caption: string;
};

type LightboxState = {
  images: LightboxImage[];
  index: number;
} | null;

type LightboxContextValue = {
  state: LightboxState;
  open: (images: LightboxImage[], index: number) => void;
  close: () => void;
  step: (dir: 1 | -1) => void;
};

const LightboxContext = createContext<LightboxContextValue | null>(null);

export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<LightboxState>(null);

  const open = useCallback((images: LightboxImage[], index: number) => {
    setState({ images, index });
  }, []);

  const close = useCallback(() => setState(null), []);

  const step = useCallback((dir: 1 | -1) => {
    setState((prev) => {
      if (!prev) return prev;
      const n = prev.images.length;
      return { ...prev, index: (prev.index + dir + n) % n };
    });
  }, []);

  const value = useMemo(
    () => ({ state, open, close, step }),
    [state, open, close, step],
  );

  return (
    <LightboxContext.Provider value={value}>
      {children}
    </LightboxContext.Provider>
  );
}

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error("useLightbox must be used within LightboxProvider");
  return ctx;
}
